import { z } from 'zod';
import { objectIdSchema } from '../validation/id';

//* location
export const addressLocationSchema = z.object({
  /** an array that represents lat & lang coordinates
   * [longitude, latitude]
   *
   * [0] = longitude
   *
   * [1] = latitude
   *
   * this structure is special for monogodb to read and query locations data properly -- DON"T CHANGE
   *
   * for more details {@link https://www.mongodb.com/docs/manual/geospatial-queries/}
   */
  coordinates: z.number().array().max(2).min(2),
  /** the type of coordinates this structure is special for monogodb to read and query locations data properly -- DON"T CHANGE
   *
   * for more details {@link https://www.mongodb.com/docs/manual/geospatial-queries/}
   */
  type: z.enum(['Point']).default('Point'),
});
export type AddressLocation = z.infer<typeof addressLocationSchema>;

export const locationPolygonSchema = z.object({
  /**
   * a Polygon is type of cordintates that represents a rectangle/box in a map and it's an array of coordinates:
   *
   * [
   *  [
   *    [longitude, latitude], [longitude, latitude], [longitude, latitude], ... ]
   *  ]
   * ]
   *
   * **IMPORTANT** when dealing with a polygon, the last element in the coordinates array `MUST` be the same as the first one
   * so that it closes the rectangle correctly.
   */
  coordinates: z.array(z.array(addressLocationSchema.shape.coordinates)).min(1).max(1),
  type: z.enum(['Polygon']).default('Polygon'),
});
export type LocationPolygon = z.infer<typeof locationPolygonSchema>;

/**
 * GeoJSON Polygon with optional inner rings (holes).
 *
 * `coordinates: [outerRing, ...holes]` where each ring is a closed list of `[lng, lat]` pairs.
 * Stricter than `locationPolygonSchema` (still only outer-ring), but with the door open for holes.
 *
 * Mongo `$geoIntersects` understands this shape natively when a `2dsphere` index is present.
 */
export const polygonGeometrySchema = z.object({
  type: z.literal('Polygon').default('Polygon'),
  /** `[outerRing, ...optionalHoles]` — at least the outer ring. */
  coordinates: z.array(z.array(addressLocationSchema.shape.coordinates)).min(1),
});
export type PolygonGeometry = z.infer<typeof polygonGeometrySchema>;

/**
 * GeoJSON MultiPolygon — many disjoint polygons stored as one shape.
 *
 * `coordinates: [[outerRing, ...holes], [outerRing, ...holes], ...]`
 *
 * Required for Saudi province / governorate boundaries that have island pieces
 * (Eastern Province, Makkah, ~30% of governorates in the Saudi master Excel).
 */
export const multiPolygonGeometrySchema = z.object({
  type: z.literal('MultiPolygon').default('MultiPolygon'),
  coordinates: z
    .array(z.array(z.array(addressLocationSchema.shape.coordinates)).min(1))
    .min(1),
});
export type MultiPolygonGeometry = z.infer<typeof multiPolygonGeometrySchema>;

/**
 * Region boundary geometry — Polygon (with optional holes) or MultiPolygon (disjoint pieces).
 *
 * Discriminated on `type`. Use this for the `boundaries` field of any document that needs
 * `$geoIntersects` lookups across irregular shapes (e.g. `Region.boundaries`). Plain
 * `locationPolygonSchema` stays the right choice for collections that genuinely only need
 * a single outer-ring rectangle.
 */
export const regionGeometrySchema = z.discriminatedUnion('type', [
  polygonGeometrySchema,
  multiPolygonGeometrySchema,
]);
export type RegionGeometry = z.infer<typeof regionGeometrySchema>;

//* component
export const addressComponentSchema = z.object({
  /** this id will come from the  destination collection.
   * If it's empty, then it means that the destination is new it has not been yet added to the destination collection
   */
  _id: objectIdSchema.optional(),
  /** display name by default its google map's long_name.
   * The reason we have this is that if we decide in the future for any reason to use diff display name
   * other than the long_name provided by google map
   */
  name: z.string().trim(),
  /** this will be coming from google example Saudi Arabia */
  long_name: z.string().trim(),
  /** this will be coming from google. example SA */
  short_name: z.string().trim(),
  /** this will be generated from us example saudi_arabia */
  customCode: z.string().trim(),
});
export type AddressComponent = z.infer<typeof addressComponentSchema>;

//* address
export const addressSchema = z.object({
  fullAddress: z.string(),
  /** the lat & lng coordinates
   *
   * structure is special for monogodb to read and query locations data properly -- DON"T CHANGE
   *
   * for more details {@link https://www.mongodb.com/docs/manual/geospatial-queries/}
   */
  location: addressLocationSchema,
  /** google map global code ... it will be coming from google map api */
  global_code: z.string().trim(),
  country: addressComponentSchema,
  city: addressComponentSchema.optional(),
  /** the state is something like California, Qassim */
  state: addressComponentSchema.optional(),
  neighborhood: addressComponentSchema.optional(),
});
export type Address = z.infer<typeof addressSchema>;

/** this mainly used for google map api */
export const latLngSchema = z.object({
  lat: z.coerce.number(),
  lng: z.coerce.number(),
});
export type LatLng = z.infer<typeof latLngSchema>;
